/**
 * siteFonts.js
 * ────────────
 * ONE loader for the web fonts named in the site tokens. The tokens store a
 * CSS font-family stack (e.g. "Lora, Georgia, serif"); nothing else loads
 * the font files, so without this the browser falls back to Times/Arial.
 *
 * Used by the editor canvas, the public site, the Site Design showcase and
 * the preset previews so all four render the same faces.
 *
 * Strategy: take the first family of every font token, drop generic and
 * system families, and inject one Google Fonts <link> per family (400/700,
 * normal + italic). Families already requested are skipped. Families that
 * Google does not host fail silently — the stack's fallbacks still apply.
 */

var GENERIC = {
	'serif': 1, 'sans-serif': 1, 'monospace': 1, 'cursive': 1, 'fantasy': 1, 'system-ui': 1,
	'ui-serif': 1, 'ui-sans-serif': 1, 'ui-monospace': 1, 'inherit': 1, 'initial': 1
};

var SYSTEM = {
	'arial': 1, 'helvetica': 1, 'helvetica neue': 1, 'georgia': 1, 'times': 1, 'times new roman': 1,
	'verdana': 1, 'tahoma': 1, 'trebuchet ms': 1, 'courier': 1, 'courier new': 1, 'segoe ui': 1,
	'-apple-system': 1, 'blinkmacsystemfont': 1, 'menlo': 1, 'monaco': 1, 'consolas': 1
};

var FONT_TOKEN_KEYS = [
	'headingFont', 'bodyFont', 'buttonFont', 'codeFont', 'codeBlockFont',
	'h1FontFamily', 'h2FontFamily', 'h3FontFamily', 'h4FontFamily', 'h5FontFamily', 'h6FontFamily'
];

var requested = {};

/**
 * First family of a CSS font-family stack, unquoted; '' when generic/system.
 *
 * @param {string} stack
 * @returns {string}
 */
export function primaryFamily(stack) {
	if (!stack || typeof stack !== 'string') return '';
	var first = stack.split(',')[0].trim().replace(/^['"]|['"]$/g, '');
	var key = first.toLowerCase();
	if (!first || GENERIC[key] || SYSTEM[key]) return '';
	return first;
}

/**
 * Unique loadable families named by the site tokens.
 *
 * @param {Object} siteSettings
 * @returns {string[]}
 */
export function collectSiteFontFamilies(siteSettings) {
	var s = siteSettings || {};
	var out = [];
	FONT_TOKEN_KEYS.forEach(function (k) {
		var fam = primaryFamily(s[k]);
		if (fam && out.indexOf(fam) === -1) out.push(fam);
	});
	return out;
}

/**
 * Google Fonts CSS2 URL for one family (regular + bold, upright + italic).
 *
 * @param {string} family
 * @returns {string}
 */
export function googleFontUrl(family) {
	return 'https://fonts.googleapis.com/css2?family=' + encodeURIComponent(family).replace(/%20/g, '+') + ':ital,wght@0,400;0,700;1,400;1,700&display=swap';
}

/**
 * Injects a stylesheet link for every font the site tokens name.
 * Safe to call repeatedly (idempotent per family).
 *
 * @param {Object} siteSettings
 * @param {Document} [doc]  defaults to window.document
 * @returns {string[]} families requested by this call
 */
export function ensureSiteFonts(siteSettings, doc) {
	doc = doc || (typeof document !== 'undefined' ? document : null);
	if (!doc || !doc.head) return [];
	var added = [];
	collectSiteFontFamilies(siteSettings).forEach(function (family) {
		if (requested[family]) return;
		var id = 'site-font-' + family.toLowerCase().replace(/[^a-z0-9]+/g, '-');
		if (!doc.getElementById(id)) {
			var link = doc.createElement('link');
			link.id = id;
			link.rel = 'stylesheet';
			link.href = googleFontUrl(family);
			doc.head.appendChild(link);
		}
		requested[family] = true;
		added.push(family);
	});
	return added;
}

/** Test hook: forget which families were requested. */
export function resetSiteFontsForTests() {
	requested = {};
}
